import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { superAdminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Plus, Search, Building2, Users, TrendingUp, Loader2, Eye } from 'lucide-react';

const Companies = () => {
  const { dialogProps, confirm } = useConfirmation();
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    pf_number: '',
    esi_number: '',
    tan: '',
    pan: '',
    admin_email: '',
    admin_password: '',
    admin_first_name: '',
    admin_last_name: '',
    license_start_date: new Date().toISOString().split('T')[0],
    license_duration_months: 12,
  });

  useEffect(() => {
    fetchCompanies();
    fetchStats();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await superAdminAPI.getCompanies({ search });
      setCompanies(response.data);
    } catch (error) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await superAdminAPI.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCompanies();
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await superAdminAPI.createCompany(formData);
      toast.success('Company created successfully');
      setIsCreateOpen(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        pf_number: '',
        esi_number: '',
        tan: '',
        pan: '',
        admin_email: '',
        admin_password: '',
        admin_first_name: '',
        admin_last_name: '',
        license_start_date: new Date().toISOString().split('T')[0],
        license_duration_months: 12,
      });
      fetchCompanies();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeactivate = async (companyId) => {
    const confirmed = await confirm({
      title: 'Deactivate Company',
      description: 'Are you sure you want to deactivate this company? All users associated with this company will lose access.',
      confirmText: 'Deactivate',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await superAdminAPI.deactivateCompany(companyId);
      toast.success('Company deactivated');
      fetchCompanies();
      fetchStats();
    } catch (error) {
      toast.error('Failed to deactivate company');
    }
  };

  const handleReactivate = async (companyId) => {
    const confirmed = await confirm({
      title: 'Reactivate Company',
      description: 'Are you sure you want to reactivate this company? All users associated with this company will regain access.',
      confirmText: 'Reactivate',
    });
    if (!confirmed) return;

    try {
      await superAdminAPI.reactivateCompany(companyId);
      toast.success('Company reactivated');
      fetchCompanies();
      fetchStats();
    } catch (error) {
      toast.error('Failed to reactivate company');
    }
  };

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Companies</h1>
          <p className="text-muted-foreground">Manage all companies on the platform</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-company-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Company</DialogTitle>
              <DialogDescription>
                Add a new company to the platform with an admin user
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCompany}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Company Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="company-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pf_number">PF Number</Label>
                    <Input
                      id="pf_number"
                      value={formData.pf_number}
                      onChange={(e) => setFormData({ ...formData, pf_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="esi_number">ESI Number</Label>
                    <Input
                      id="esi_number"
                      value={formData.esi_number}
                      onChange={(e) => setFormData({ ...formData, esi_number: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tan">TAN</Label>
                    <Input
                      id="tan"
                      value={formData.tan}
                      onChange={(e) => setFormData({ ...formData, tan: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan">PAN</Label>
                    <Input
                      id="pan"
                      value={formData.pan}
                      onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                    />
                  </div>
                </div>
                <div className="border-t pt-4 mt-2">
                  <h4 className="font-medium mb-3">License Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="license_start_date">License Start Date *</Label>
                      <Input
                        id="license_start_date"
                        type="date"
                        value={formData.license_start_date}
                        onChange={(e) => setFormData({ ...formData, license_start_date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="license_duration_months">Duration (Months) *</Label>
                      <Input
                        id="license_duration_months"
                        type="number"
                        min="1"
                        value={formData.license_duration_months}
                        onChange={(e) => setFormData({ ...formData, license_duration_months: parseInt(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="border-t pt-4 mt-2">
                  <h4 className="font-medium mb-3">Company Admin Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin_first_name">First Name *</Label>
                      <Input
                        id="admin_first_name"
                        value={formData.admin_first_name}
                        onChange={(e) => setFormData({ ...formData, admin_first_name: e.target.value })}
                        required
                        data-testid="admin-first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin_last_name">Last Name *</Label>
                      <Input
                        id="admin_last_name"
                        value={formData.admin_last_name}
                        onChange={(e) => setFormData({ ...formData, admin_last_name: e.target.value })}
                        required
                        data-testid="admin-last-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin_email">Admin Email *</Label>
                      <Input
                        id="admin_email"
                        type="email"
                        value={formData.admin_email}
                        onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                        required
                        data-testid="admin-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin_password">Admin Password *</Label>
                      <Input
                        id="admin_password"
                        type="password"
                        value={formData.admin_password}
                        onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                        required
                        data-testid="admin-password-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createLoading} data-testid="submit-company-btn">
                  {createLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Company'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Companies</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_companies}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Companies</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.active_companies}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_employees}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_users}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      {/* Companies Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>PAN</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>License Left</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                    <TableCell className="font-medium">{company.name}</TableCell>
                    <TableCell>{company.email || '-'}</TableCell>
                    <TableCell>{company.pan || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={company.is_active ? 'default' : 'secondary'}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (!company.license_start_date) return '-';
                        const start = new Date(company.license_start_date);
                        const duration = company.license_duration_months || 12;
                        const expiry = new Date(start);
                        expiry.setMonth(start.getMonth() + duration);

                        const now = new Date();
                        const diffTime = expiry - now;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays < 0) return <span className="text-destructive font-medium">Expired</span>;
                        if (diffDays <= 30) return <span className="text-amber-600 font-medium">{diffDays} days</span>;
                        return <span>{diffDays} days</span>;
                      })()}
                    </TableCell>
                    <TableCell>
                      {new Date(company.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/superadmin/company/${company.id}`}>
                          <Button variant="outline" size="sm" data-testid={`view-company-${company.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        {company.is_active ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeactivate(company.id)}
                            data-testid={`deactivate-company-${company.id}`}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-primary hover:text-primary"
                            onClick={() => handleReactivate(company.id)}
                            data-testid={`reactivate-company-${company.id}`}
                          >
                            Activate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfirmationDialog {...dialogProps} />
    </div>
  );
};

export default Companies;
